import { motion } from "framer-motion";
import Booking from "@/components/Booking";
import { ArrowUpRight, Compass, Heart, Scale, Route, FolderKanban, UserPlus, ClipboardCheck, FileText, MessagesSquare } from "lucide-react";

const bookMeeting = () => document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" });

const TESTS = [
  { icon: Heart, title: "Τεστ Επαγγελματικών Ενδιαφερόντων", desc: "Βασίζεται στη θεωρία Holland — συνδέει τις κλίσεις σου με επαγγελματικά περιβάλλοντα.", href: "https://e-stadiodromia.eoppep.gr/ergaleia/test-endiaferonton/" },
  { icon: Scale, title: "Τεστ Εργασιακών Αξιών", desc: "9 κλίμακες αξιών: τι σε γεμίζει πραγματικά σε μια δουλειά (ασφάλεια, δημιουργικότητα, αυτονομία…).", href: "https://e-stadiodromia.eoppep.gr/ergaleia/test-axion/" },
  { icon: Route, title: "Τεστ Επαγγελματικών Αποφάσεων", desc: "Πώς παίρνεις αποφάσεις για σπουδές & επάγγελμα — και πού κολλάς.", href: "https://e-stadiodromia.eoppep.gr/ergaleia/test-epangelmatikon-apofaseon/" },
  { icon: FolderKanban, title: "Ατομικός Φάκελος (e-Portfolio)", desc: "Συγκέντρωσε αποτελέσματα, στόχους και σχέδιο σταδιοδρομίας σε ένα μέρος.", href: "https://e-stadiodromia.eoppep.gr/ergaleia/atomikos-fakelos-schediasmou-stadiodromias-e-portfolio/" },
];

const STEPS = [
  { icon: UserPlus, t: "Φτιάξε δωρεάν λογαριασμό", d: "στην πύλη e-stadiodromia.eoppep.gr (χρειάζεται μόνο email)." },
  { icon: ClipboardCheck, t: "Συμπλήρωσε τα τεστ", d: "με ηρεμία, 15–20' το καθένα — δεν υπάρχουν «σωστές» απαντήσεις." },
  { icon: FileText, t: "Κατέβασε την έκθεση", d: "εξατομικευμένα αποτελέσματα σε PDF με κείμενο & διαγράμματα." },
  { icon: MessagesSquare, t: "Συζήτησέ τα μαζί μας", d: "τα συνδυάζουμε με τις Βάσεις & τον υπολογιστή μορίων για ρεαλιστικούς στόχους." },
];

export default function CareerGuidance() {
  return (
    <section id="career" data-testid="career-section" className="py-20 md:py-32 bg-secondary/40">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="font-mono-accent text-xs uppercase tracking-[0.2em] text-accent mb-4">// Επαγγελματικός Προσανατολισμός</div>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight flex items-center gap-3">
              <Compass className="h-8 w-8 text-primary" strokeWidth={1.5} /> Ποιος είσαι, πριν διαλέξεις τι θα σπουδάσεις.
            </h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Ο ΕΟΠΠΕΠ (Εθνικός Οργανισμός Πιστοποίησης Προσόντων & Επαγγελματικού Προσανατολισμού) προσφέρει <strong>δωρεάν, επιστημονικά σταθμισμένα τεστ</strong> για μαθητές Γυμνασίου–Λυκείου. Κάνε τα και φέρε τα αποτελέσματα — τα συζητάμε μαζί.
            </p>
          </div>
          <a
            data-testid="career-cta-eoppep"
            href="https://e-stadiodromia.eoppep.gr/ergaleia/"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 shrink-0 bg-primary text-primary-foreground font-mono-accent text-xs uppercase tracking-widest px-6 py-3.5 rounded-sm hover:bg-accent transition-colors"
          >
            Κάνε το τεστ του ΕΟΠΠΕΠ
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 grid sm:grid-cols-2 gap-4">
            {TESTS.map((t, i) => {
              const Icon = t.icon;
              return (
                <motion.a
                  key={t.title}
                  href={t.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`career-test-${i}`}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="group flex flex-col bg-white border border-border rounded-sm p-5 hover:border-primary transition-colors"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="h-10 w-10 flex items-center justify-center rounded-sm bg-primary/10 text-primary"><Icon className="h-5 w-5" strokeWidth={1.5} /></span>
                    <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  <h3 className="font-heading font-bold text-base leading-snug group-hover:text-primary transition-colors">{t.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed flex-1">{t.desc}</p>
                  <div className="mt-4 pt-3 border-t border-border font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground">Δωρεάν · ΕΟΠΠΕΠ</div>
                </motion.a>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.2 }}
            data-testid="career-steps"
            className="lg:col-span-2 bg-primary text-primary-foreground rounded-sm p-6 md:p-8 flex flex-col"
          >
            <div className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-white/60 mb-5">Πώς γίνεται — 4 βήματα</div>
            <ol className="space-y-5 flex-1">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                return (
                  <li key={i} className="flex gap-4">
                    <span className="h-9 w-9 shrink-0 flex items-center justify-center rounded-sm bg-white/10 font-heading font-extrabold">{i + 1}</span>
                    <div>
                      <div className="font-heading font-bold leading-snug flex items-center gap-2"><Icon className="h-4 w-4 text-accent" /> {s.t}</div>
                      <div className="text-sm text-white/70 leading-relaxed mt-1">{s.d}</div>
                    </div>
                  </li>
                );
              })}
            </ol>
            <div className="mt-8 pt-6 border-t border-white/15 flex flex-col sm:flex-row gap-3">
              <a
                data-testid="career-guide-link"
                href="https://e-stadiodromia.eoppep.gr/odigies-prosvasis-sta-erotimatologia-epangelmatikou-prosanatolismou/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 font-mono-accent text-[10px] uppercase tracking-widest border border-white/30 px-4 py-3 rounded-sm hover:bg-white/10 transition-colors"
              >
                Οδηγίες πρόσβασης <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
              <button
                data-testid="career-contact-btn"
                onClick={bookMeeting}
                className="inline-flex items-center justify-center gap-2 font-mono-accent text-[10px] uppercase tracking-widest bg-white text-primary px-4 py-3 rounded-sm hover:bg-accent hover:text-white transition-colors"
              >
                Κλείσε συνάντηση προσανατολισμού
              </button>
            </div>
          </motion.div>
        </div>

        <div className="mt-6">
          <Booking />
        </div>

        <p className="mt-6 text-xs text-muted-foreground leading-relaxed">
          * Τα τεστ αποτελούν ενδείξεις που ενισχύουν τη συμβουλευτική διαδικασία και δεν υποκαθιστούν τη συζήτηση με έναν σύμβουλο. Πηγή: Εθνική Πύλη Διά Βίου Συμβουλευτικής Σταδιοδρομίας ΕΟΠΠΕΠ (e-stadiodromia.eoppep.gr).
        </p>
      </div>
    </section>
  );
}
